// What each plan actually gets.
//
// Every line here is a claim we make for money, so each one is tied to a gate the Worker enforces
// rather than to marketing copy. The file references are the code that has to change if a line
// changes — and if a gate is removed, the line must come off this list the same day.
//
// Two claims made elsewhere in the product are deliberately absent:
//   • "the deepest portrait" — depth.ts gates purely on entry counts (3+3 taking shape, 5+5 deep
//     portrait), identically for free and paid. Paying does not deepen a portrait; writing does.
//   • "a monthly letter" — no recurring letter exists. reward-catalog.ts grants one at 25 entries,
//     which every free account also receives.
//
// **double asterisks** mark the number or noun carrying the claim, rendered in full-strength ink.

export const FREE_FEATURES: readonly string[] = [
  'Unlimited entries, waking and dream',
  '**Encrypted on your device**, synced to your phone',
  'Your archetype portrait, deepening as you write',
  'Symbol Vault, and the links between entries',
  '**3 AI replies a month** for dreams and chat',
  '**One** Dream Vision with one recreation to try',
  'Daily Wisdom, and voice on your device',
];

export const PREMIUM_FEATURES: readonly string[] = [
  'Dream readings & follow-up conversations within **monthly fair use**',
  'Up to **10** Dream Visions a month, each with one recreation',
  'The **Weekly Mirror**, after each week closes',
  'Discuss any observation with Oreia',
  'Dream details, drawn out automatically',
  'Up to **10,000 characters** of cloud speech a month',
  'Every atmosphere',
];

export const AI_FAIR_USE = 'Readings, chat, images, cloud speech and automatic analysis share a monthly AI allowance. Image credits and speech allowances are maximums, not separate unlimited budgets. Allowances renew monthly and do not roll over. On-device speech, writing and saved entries stay available. Existing subscribers keep their price and 30 DreamVision credits.';

export interface Segment {
  text: string;
  strong: boolean;
}

/**
 * Splits a feature line on its **emphasis** markers.
 *
 * Deliberately not a markdown parser: it handles exactly the one thing these strings use, so an
 * unclosed marker degrades to visible asterisks in review rather than swallowing the rest of a line.
 */
export function segments(line: string): Segment[] {
  return line
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part !== '')
    .map((part) =>
      part.startsWith('**') && part.endsWith('**')
        ? { text: part.slice(2, -2), strong: true }
        : { text: part, strong: false },
    );
}
